import type { MergeConnectSetupSelections } from "~integrations/helpers/accountingConfig";
import type { GrowPointConfig, MergeCrmConfig } from "~integrations/types";
import type { IntegrationSettings } from "~integrations/wizard/integrationSettings";
import type { IntegrationType } from "~integrations/wizard/SelectIntegration";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import accountingConfig from "~integrations/helpers/accountingConfig";

export interface IntegrationWizardState {
	accountName: string;
	accountingSetup: MergeConnectSetupSelections;
	activeStep: number;
	createdIntegrationId?: string;
	createError?: string;
	crmConfig: MergeCrmConfig;
	growPointConfig: GrowPointConfig;
	isConnected: boolean;
	isCreating: boolean;
	mergeLinkPublicToken?: string;
	mergeReconnectPending: boolean;
	selectedIntegration?: IntegrationType;
	settings: IntegrationSettings;
}

const initialState: IntegrationWizardState = {
	accountName: "",
	accountingSetup: accountingConfig.defaultMergeConnectSetup(),
	activeStep: 0,
	crmConfig: {
		accountToken: "",
		apiKey: "",
		baseUrl: ""
	},
	growPointConfig: {
		database: "",
		endpoint: "",
		password: "",
		username: ""
	},
	isConnected: false,
	isCreating: false,
	mergeReconnectPending: false,
	settings: {
		autoRetry: true,
		notifyOnError: true,
		syncCustomers: true,
		syncFrequency: "realtime",
		syncInvoices: true,
		syncItems: true
	}
};

export const integrationWizardSlice = createSlice({
	initialState,
	name: "integrationWizard",
	reducers: {
		resetWizard: () => initialState,
		setAccountingSetup: (state, action: PayloadAction<MergeConnectSetupSelections>) => {
			state.accountingSetup = action.payload;
		},
		setAccountName: (state, action: PayloadAction<string>) => {
			state.accountName = action.payload;
		},
		setActiveStep: (state, action: PayloadAction<number>) => {
			state.activeStep = action.payload;
		},
		setCreatedIntegrationId: (state, action: PayloadAction<string | null>) => {
			state.createdIntegrationId = action.payload ?? undefined;
		},
		setCreateError: (state, action: PayloadAction<string | null>) => {
			state.createError = action.payload ?? undefined;
		},
		setCrmConfig: (state, action: PayloadAction<MergeCrmConfig>) => {
			state.crmConfig = action.payload;
		},
		setGrowPointConfig: (state, action: PayloadAction<GrowPointConfig>) => {
			state.growPointConfig = action.payload;
		},
		setIsConnected: (state, action: PayloadAction<boolean>) => {
			state.isConnected = action.payload;
		},
		setIsCreating: (state, action: PayloadAction<boolean>) => {
			state.isCreating = action.payload;
		},
		setMergeLinkPublicToken: (state, action: PayloadAction<string | undefined>) => {
			state.mergeLinkPublicToken = action.payload;
		},
		setMergeReconnectPending: (state, action: PayloadAction<boolean>) => {
			state.mergeReconnectPending = action.payload;
		},
		setSelectedIntegration: (state, action: PayloadAction<IntegrationType | null>) => {
			state.selectedIntegration = action.payload ?? undefined;
		},
		setSettings: (state, action: PayloadAction<IntegrationSettings>) => {
			state.settings = action.payload;
		},
		updateSettings: (state, action: PayloadAction<Partial<IntegrationSettings>>) => {
			state.settings = { ...state.settings, ...action.payload };
		}
	}
});

export const {
	resetWizard,
	setAccountingSetup,
	setAccountName,
	setActiveStep,
	setCreatedIntegrationId,
	setCreateError,
	setCrmConfig,
	setGrowPointConfig,
	setIsConnected,
	setIsCreating,
	setMergeLinkPublicToken,
	setMergeReconnectPending,
	setSelectedIntegration,
	setSettings,
	updateSettings
} = integrationWizardSlice.actions;

export default integrationWizardSlice.reducer;
